import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiMapPin,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import { MdOutlineTravelExplore } from "react-icons/md";
import { RiCustomerService2Fill } from "react-icons/ri";

const serviceHighlights = [
  {
    title: "Local Safari Experts",
    text: "Routes planned around seasons, permits, park access, and wildlife movement.",
    icon: MdOutlineTravelExplore,
  },
  {
    title: "Responsive Guest Care",
    text: "Support before, during, and after your safari with clear trip coordination.",
    icon: RiCustomerService2Fill,
  },
];

const quickBenefits = [
  "Gorilla and chimpanzee permit support",
  "Private guides and safari vehicles",
  "Luxury, midrange, and family-friendly options",
];

const Search = () => {
  return (
    <section className="bg-white py-20 font-[Poppins]">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
        <div className="flex flex-col justify-center">
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#C88804]">
            Plan With Confidence
          </p>

          <h2 className="mt-4 max-w-3xl text-4xl font-extrabold uppercase leading-tight tracking-normal text-[#082f1f] sm:text-5xl">
            Your Adventure Begins With A Well-Planned Safari
          </h2>

          <p className="mt-5 max-w-3xl text-base leading-8 text-[#5d7168] sm:text-lg">
            Discover East Africa through carefully curated wildlife journeys,
            gorilla trekking experiences, cultural encounters, and beach
            extensions. We help you choose the right route, pace, lodge style,
            and travel dates for a smoother safari from start to finish.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {serviceHighlights.map((service) => {
              const Icon = service.icon;

              return (
                <div
                  key={service.title}
                  className="rounded-sm border border-[#eadfbe] bg-[#fbf8f0] p-5 transition hover:-translate-y-1 hover:shadow-xl hover:shadow-black/10"
                >
                  <div className="mb-4 grid h-12 w-12 place-items-center rounded-sm bg-[#082f1f] text-3xl text-[#F5B731]">
                    <Icon />
                  </div>
                  <h3 className="text-xl font-extrabold text-[#082f1f]">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-[#5d7168]">
                    {service.text}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 grid gap-3">
            {quickBenefits.map((benefit) => (
              <div key={benefit} className="flex items-center gap-3 text-sm text-[#315245]">
                <FiCheckCircle className="text-lg text-[#C88804]" />
                {benefit}
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 hidden bg-[#082f1f] lg:block" />

          <div className="relative rounded-sm bg-white p-6 shadow-2xl shadow-black/15">
            <div className="border-l-4 border-[#F5B731] bg-[#f7f2e7] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C88804]">
                Safari Enquiry
              </p>
              <h3 className="mt-2 text-2xl font-extrabold text-[#082f1f]">
                Start Planning Your Trip
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#5d7168]">
                Share your preferred destination and travel date. Our team will
                help match you with the best route and safari style.
              </p>
            </div>

            <form className="mt-6 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-bold text-[#082f1f]">
                  Destination
                </label>
                <div className="relative">
                  <FiMapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C88804]" />
                  <select className="h-12 w-full appearance-none rounded-sm border border-[#d9cda9] bg-white pl-10 pr-4 text-sm text-[#315245] outline-none transition focus:border-[#F5B731] focus:ring-2 focus:ring-[#F5B731]/30">
                    <option>Uganda Gorilla Safari</option>
                    <option>Kenya Wildlife Safari</option>
                    <option>Tanzania Migration Safari</option>
                    <option>Rwanda Gorilla Trekking</option>
                    <option>Zanzibar Beach Extension</option>
                    <option>Multi-Country East Africa</option>
                  </select>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-bold text-[#082f1f]">
                    Travel Date
                  </label>
                  <div className="relative">
                    <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C88804]" />
                    <input
                      type="date"
                      className="h-12 w-full rounded-sm border border-[#d9cda9] bg-white pl-10 pr-4 text-sm text-[#315245] outline-none transition focus:border-[#F5B731] focus:ring-2 focus:ring-[#F5B731]/30"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-bold text-[#082f1f]">
                    Travelers
                  </label>
                  <div className="relative">
                    <FiUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C88804]" />
                    <select className="h-12 w-full appearance-none rounded-sm border border-[#d9cda9] bg-white pl-10 pr-4 text-sm text-[#315245] outline-none transition focus:border-[#F5B731] focus:ring-2 focus:ring-[#F5B731]/30">
                      <option>1 Traveler</option>
                      <option>2 Travelers</option>
                      <option>3-4 Travelers</option>
                      <option>5-8 Travelers</option>
                      <option>Group Tour</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-[#082f1f]">
                  Travel Style
                </label>
                <select className="h-12 w-full rounded-sm border border-[#d9cda9] bg-white px-4 text-sm text-[#315245] outline-none transition focus:border-[#F5B731] focus:ring-2 focus:ring-[#F5B731]/30">
                  <option>Comfort / Midrange</option>
                  <option>Luxury Safari</option>
                  <option>Family Friendly</option>
                  <option>Adventure Focused</option>
                  <option>Honeymoon / Private Escape</option>
                </select>
              </div>

              <Link
                to="/contact"
                className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-sm bg-[#082f1f] px-5 py-3 text-center text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-[#F5B731] hover:text-[#082f1f]"
              >
                Check Availability
                <FiArrowRight />
              </Link>
            </form>

            <div className="mt-6 flex items-start gap-3 rounded-sm bg-[#082f1f] p-4 text-white">
              <FiShield className="mt-1 shrink-0 text-xl text-[#F5B731]" />
              <p className="text-sm leading-6 text-white/75">
                No instant booking pressure. We review your request and recommend
                a realistic itinerary, route, and budget range.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Search;
